/**
 * An uploader with multipart, resumption support.
 */

import { RpxResponse, rpx } from './rpx';
import { stripSVG } from 'shared/media';

export type UploadState = {
  status: 'init' | 'uploading' | 'finishing' | 'done' | 'failed';
  progress: number;
};

type UploadOpts = {
  file: Blob;
  name?: string;
  isPublic?: boolean;
  onProgress(state: UploadState): void;
};

type UploadContext = RpxResponse<typeof rpx.files.beginUpload> & {
  /**
   * A map of upload part index to etag.
   */
  etags: Map<number, string>;
  /**
   * A map of upload part index to the number of bytes sent so far.
   */
  sentBytes: Map<number, number>;
  /**
   * The whole-number percentage last reported, so progress events don't re-render on every tick.
   */
  reportedPercent: number;
  /**
   * The part requests in flight, so abort can cancel them.
   */
  requests: Set<XMLHttpRequest>;
  /**
   * The current run of part uploads, so a retry waits for an aborted run to settle.
   */
  running?: Promise<unknown>;
  file: Blob;
  name: string;
  parts: Array<{ partNumber: number; url: string }>;
  terminated: boolean;
};

function putBlob(opts: {
  url: string;
  type: string;
  body: Blob;
  requests: Set<XMLHttpRequest>;
  onSent(bytes: number): void;
}): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    opts.requests.add(xhr);
    xhr.onloadend = () => opts.requests.delete(xhr);
    xhr.open('PUT', opts.url);
    xhr.setRequestHeader('Content-Type', opts.type);
    xhr.upload.onprogress = (e) => opts.onSent(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 400) {
        reject(new Error(xhr.responseText || `Upload failed with status ${xhr.status}`));
        return;
      }
      // ETag is wrapped in quotes, but the finalization call needs this to be unquoted
      // so, slice(1, -1) removes the quotes.
      resolve(xhr.getResponseHeader('ETag')?.slice(1, -1) || '');
    };
    xhr.onerror = () => reject(new Error('The upload was interrupted. Check your connection.'));
    xhr.onabort = () => reject(new Error('The upload was cancelled.'));
    xhr.send(opts.body);
  });
}

/**
 * In a multipart upload, this uploads the specified part.
 * Uses XHR rather than fetch so large single-part uploads can report progress.
 */
async function uploadPart(opts: {
  file: Blob;
  partSize: number;
  partNumber: number;
  url: string;
  requests: Set<XMLHttpRequest>;
  isTerminated(): boolean;
  onSent(bytes: number): void;
}) {
  const start = opts.partNumber * opts.partSize;
  const end = Math.min(start + opts.partSize, opts.file.size);
  const body = opts.file.slice(start, end);
  const numRetries = 3;
  let error: any;
  for (let i = 0; i < numRetries; ++i) {
    try {
      opts.onSent(0);
      return await putBlob({
        url: opts.url,
        type: opts.file.type,
        body,
        requests: opts.requests,
        onSent: opts.onSent,
      });
    } catch (err) {
      if (opts.isTerminated()) {
        throw err;
      }
      error = err;
      console.error(err);
    }
  }
  throw error;
}

function getFileName(blob: Blob, defaultName = 'unnamedfile') {
  return (blob as File).name || defaultName;
}

async function sanitizeFile(blob: Blob) {
  if (!blob.type.toLowerCase().includes('svg')) {
    return blob;
  }
  const svg = await blob.text();
  const stripped = stripSVG(svg);
  return new File([stripped], getFileName(blob, 'sanitized.svg'), { type: blob.type });
}

function getProgress(ctx: UploadContext) {
  if (!ctx.file.size) {
    return ctx.presignedUrls.length ? (ctx.etags.size / ctx.presignedUrls.length) * 100 : 0;
  }
  let sent = 0;
  ctx.sentBytes.forEach((bytes) => {
    sent += bytes;
  });
  return Math.min(100, (sent / ctx.file.size) * 100);
}

async function makeUploadContext(opts: UploadOpts): Promise<UploadContext> {
  const file = await sanitizeFile(opts.file);
  const name = opts.name || getFileName(opts.file);
  opts.onProgress({ status: 'init', progress: 0 });
  const ctx = await rpx.files.beginUpload({
    size: file.size,
    type: file.type,
    name,
  });
  return {
    ...ctx,
    terminated: false,
    file,
    name,
    parts: ctx.presignedUrls.map((url, partNumber) => ({ url, partNumber })),
    etags: new Map(),
    sentBytes: new Map(),
    reportedPercent: -1,
    requests: new Set(),
  };
}

async function uploadParts(opts: UploadOpts, ctx: UploadContext) {
  while (ctx.parts.length && !ctx.terminated) {
    const part = ctx.parts.shift();
    if (part == null) {
      break;
    }
    try {
      const etag = await uploadPart({
        ...part,
        file: ctx.file,
        partSize: ctx.partSize,
        requests: ctx.requests,
        isTerminated: () => ctx.terminated,
        onSent(bytes) {
          ctx.sentBytes.set(part.partNumber, bytes);
          const progress = getProgress(ctx);
          if (!ctx.terminated && Math.floor(progress) !== ctx.reportedPercent) {
            ctx.reportedPercent = Math.floor(progress);
            opts.onProgress({ status: 'uploading', progress });
          }
        },
      });
      ctx.etags.set(part.partNumber + 1, etag);
      ctx.sentBytes.set(
        part.partNumber,
        Math.min(ctx.partSize, ctx.file.size - part.partNumber * ctx.partSize),
      );
      opts.onProgress({
        status: ctx.terminated ? 'failed' : 'uploading',
        progress: getProgress(ctx),
      });
    } catch (err) {
      ctx.sentBytes.delete(part.partNumber);
      ctx.parts.push(part);
      if (!ctx.terminated) {
        ctx.terminated = true;
        ctx.requests.forEach((xhr) => xhr.abort());
        throw err;
      }
    }
  }
}

/**
 * Create an uploader object which handles progress, resumption, and can abort a multipart
 * upload prior to completion.
 */
export function makeUploader(opts: UploadOpts) {
  const parallelism = 4;
  let uploadContext: UploadContext | undefined;

  return {
    async upload() {
      uploadContext ||= await makeUploadContext(opts);
      const ctx = uploadContext;
      await ctx.running;
      ctx.terminated = false;
      opts.onProgress({ status: 'uploading', progress: getProgress(ctx) });
      const workers = new Array(parallelism).fill(0).map(() => uploadParts(opts, ctx));
      ctx.running = Promise.allSettled(workers);
      await Promise.all(workers);
      if (ctx.terminated) {
        return;
      }
      opts.onProgress({ status: 'finishing', progress: 100 });
      const result = await rpx.files.finishUpload({
        fileId: ctx.fileId,
        type: ctx.file.type,
        name: ctx.name,
        isPublic: opts.isPublic,
        uploadId: ctx.uploadId,
        filePath: ctx.filePath,
        etags: Array.from(ctx.etags.entries())
          .map(([k, v]) => ({ part: k, etag: v }))
          .sort((a, b) => a.part - b.part),
      });
      // Clear upload info before we notify of completion. This way, abort is a noop
      ctx.terminated = true;
      opts.onProgress({ status: 'done', progress: 100 });
      return {
        name: getFileName(ctx.file, ctx.name),
        type: ctx.file.type,
        size: ctx.file.size,
        ...result,
      };
    },

    async abort() {
      if (!uploadContext || uploadContext.terminated) {
        return;
      }
      uploadContext.terminated = true;
      uploadContext.requests.forEach((xhr) => xhr.abort());
    },
  };
}
